// src/pages/seeker/SeekerSummary.jsx
// Professional Summary — Brief Summary (500 chars) + Full Summary (2000 chars)

import React, { useEffect, useState } from 'react';
import { seekerAPI } from '../../services/api';
import toast from 'react-hot-toast';

const C = {
  navy: '#0e4e95', blue: '#3D80CC', white: '#ffffff',
  offwhite: '#f4f7fb', text: '#1a1a1a', muted: '#666',
  border: '#d0daea',
};

const SectionTitle = ({ children }) => (
  <div style={{ fontSize: '14px', fontWeight: 700, color: '#E8EDF2', background: '#4A6FA5', padding: '8px 12px', borderRadius: '3px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </div>
);

const CharCount = ({ current, max }) => {
  const pct = current / max;
  const color = pct > 0.9 ? '#CC0000' : pct > 0.75 ? '#b45309' : C.muted;
  return (
    <div style={{ fontSize: '11px', color, textAlign: 'right', marginTop: '4px' }}>
      {current} / {max}
    </div>
  );
};

export const SeekerSummary = () => {
  const [loading,  setLoading]  = useState(true);
  const [brief,    setBrief]    = useState('');
  const [full,     setFull]     = useState('');

  const loadAll = async () => {
    try {
      const res = await seekerAPI.getProfile();
      const p   = res.data.data;
      setBrief(p.summary      || '');
      setFull( p.fullSummary  || '');
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const saveField = async (field, val) => {
    try {
      await seekerAPI.updateProfile({ [field]: val === '' ? null : val });
      toast.success('Saved', { duration: 1200, id: 'autosave' });
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Save failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Loading…</div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Professional Summary</h1>
        <p>Changes are saved automatically when you leave each field</p>
      </div>

      <div className="page-content">

        {/* ── Brief Summary ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '16px' }}>
          <SectionTitle>Brief Summary</SectionTitle>
          <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 12px' }}>
            A concise overview used in search results and candidate cards. Keep it punchy — 2 to 4 sentences.
          </p>
          <textarea
            value={brief}
            onChange={e => setBrief(e.target.value)}
            onBlur={e => saveField('summary', e.target.value)}
            placeholder="e.g. Experienced accountant with 8+ years in financial reporting and analysis…"
            maxLength={500}
            rows={4}
            className="form-input"
            style={{ resize: 'vertical' }}
          />
          <CharCount current={(brief || '').length} max={500} />
        </div>

        {/* ── Full Summary ── */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '6px', padding: '20px 24px', marginBottom: '40px' }}>
          <SectionTitle>Full Summary</SectionTitle>
          <p style={{ fontSize: '12px', color: C.muted, margin: '0 0 12px' }}>
            A detailed professional profile covering your background, expertise, and career goals. Used on your full CV and profile page.
          </p>
          <textarea
            value={full}
            onChange={e => setFull(e.target.value)}
            onBlur={e => saveField('fullSummary', e.target.value)}
            placeholder="Describe your professional background, key achievements, and what you're looking for in your next role…"
            maxLength={2000}
            rows={10}
            className="form-input"
            style={{ resize: 'vertical' }}
          />
          <CharCount current={(full || '').length} max={2000} />
        </div>

      </div>
    </div>
  );
};
